import { useMergeRefs } from "@floating-ui/react";
import { observer } from "mobx-react-lite";

import { SuggestionIcon } from "@/components/suggestions/icon/SuggestionIcon";
import { libraryRingClass } from "@/components/suggestions/list/utils";
import { useListItemProps } from "@/components/suggestions/navigation/useListItemProps";
import { useScrollActiveIntoView } from "@/components/suggestions/navigation/useScrollActiveIntoView";
import { useTooltip } from "@/components/tooltip/Tooltip";
import { displayHotkey } from "@/utils/hotkeys";
import { twMerge } from "@/utils/twMerge";

import type { Suggestion } from "@/components/suggestions/types";
import type { DocumentModel } from "@/models/DocumentModel";
import type { SuggestionsModel } from "@/models/SuggestionsModel";
import type { ComponentProps, ReactNode } from "react";

type Props = ComponentProps<"button"> & {
  index: number;
  doc: DocumentModel;
  suggestions: SuggestionsModel;
  suggestion: Suggestion;
  action?: React.ReactNode;
  isHighlighted?: boolean;
  disableHoverStyles?: boolean;
  listId: string | null;
};

export const SuggestionGridOption = observer(function SuggestionGridOption({
  ref: forwardedRef,
  doc,
  index,
  suggestion,
  suggestions,
  action: actionProp,
  isHighlighted,
  disableHoverStyles,
  className,
  listId,
  ...props
}: Props) {
  const isActive = suggestions.isActive(index, listId);

  let label: string | null = null;
  let icon: ReactNode = null;
  let tooltipTitle: string | null = null;

  const action = isActive ? displayHotkey("active-suggestion") : actionProp;

  if (suggestion.type === "single") {
    if (suggestion.item.isSymbol) {
      const codePoint = suggestion.item.symbol.codePointAt(0);
      icon = codePoint != null ? String.fromCodePoint(codePoint) : null;
      const { displayName, category } = suggestion.item;
      const categoryText = Array.isArray(category) ? category.join(", ") : category;
      if (displayName && categoryText) {
        tooltipTitle = `${displayName} · ${categoryText}`;
      } else if (displayName) {
        tooltipTitle = displayName;
      }
    } else {
      label = suggestion.item.displayName ?? null;
      icon = <SuggestionIcon suggestion={suggestion} doc={doc} />;
    }
  }

  const getItemProps = useListItemProps({
    index,
    listId,
    suggestion,
    isActive,
  });

  const scrollRef = useScrollActiveIntoView<HTMLButtonElement>(isActive, suggestions, forwardedRef);

  const { handlerProps: tooltipHandlerProps, tooltip } = useTooltip({
    title: tooltipTitle,
    placement: "top",
    disabled: !tooltipTitle,
  });

  const { ref: tooltipRef, ...tooltipInteractionProps } = tooltipHandlerProps as {
    ref?: (node: HTMLButtonElement | null) => void;
  } & Record<string, unknown>;

  const setRef = useMergeRefs([scrollRef, tooltipRef]);

  if (!label && !icon) return null;

  return (
    <>
      <button
        ref={setRef}
        type="button"
        aria-label={tooltipTitle ?? undefined}
        className={twMerge(
          "flex gap-1 items-center h-8.5 w-full px-2 rounded-8 data-[focus]:bg-blue-100 data-[focus]:dark:bg-blue-900",
          !disableHoverStyles && "hover:bg-blue-100 dark:hover:bg-gray-900",
          isHighlighted && "bg-gray-100 dark:bg-gray-800",
          isActive && "bg-blue-100 dark:bg-gray-900",
          libraryRingClass(suggestion),
          className,
        )}
        {...tooltipInteractionProps}
        {...getItemProps(props)}
      >
        <span className="font-mono">{icon}</span>
        <span className="text-size-14">{label}</span>
        {action && (
          <div className="font-sans self-start ml-auto -mr-0.5 pt-1.5 text-xxs text-gray-400">
            {action}
          </div>
        )}
      </button>
      {tooltip}
    </>
  );
});
